import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '@dispatch/web/ui';
import { BulkTaskActionDrawer } from '@dispatch/web/features/tasks';
import { BulkTaskLockResponse, TaskInfo } from '@dispatch/shared/types';
import { useBulkLockTasks } from '@dispatch/shared/hooks';

const taskWord = (n: number) => (n === 1 ? 'task' : 'tasks');

const filterLockableRow = (row: TaskInfo): boolean => {
  if (row.targetQueue === 'egress' || row.targetQueue === 'cancelled_q') {
    return false;
  }
  if (row.lockedAt != null) return false;
  if (!row.assignedResourceId) return false;
  return true;
};

const formatOutcomeBreakdown = (data: BulkTaskLockResponse): string => {
  const parts: string[] = [];
  if (data.skippedCount > 0) parts.push(`${data.skippedCount} skipped`);
  if (data.failedCount > 0) parts.push(`${data.failedCount} failed`);
  return parts.join(', ');
};

// Wrapped in <span> blocks (rendered display: block) instead of <p> because
// ConfirmModal renders confirmContent inside <Typography component="p">; an
// inner <p> triggers an invalid-DOM-nesting warning and is stripped by
// some browsers.
const renderLockContent = (selectedCount: number): React.ReactElement => (
  <>
    <span style={{ display: 'block', marginBottom: 8 }}>
      Dispatch will lock these tasks and alert the resource.
    </span>
    <span style={{ display: 'block' }}>
      <b>{`${selectedCount} ${taskWord(selectedCount)}`}</b> will be locked.
    </span>
  </>
);

export const BulkTaskLock = () => {
  const navigate = useNavigate();
  const { notifySuccess, notifyWarning, notifyError } = useNotification();

  const { mutate: bulkLock, status: bulkLockStatus } = useBulkLockTasks(
    (data: BulkTaskLockResponse) => {
      if (data.lockedCount > 0) {
        notifySuccess(
          '',
          `${data.lockedCount} ${taskWord(data.lockedCount)} locked`
        );
      }

      // If any task was skipped or failed, surface a secondary toast with the
      // breakdown so the dispatcher knows the full operation outcome.
      if (data.skippedCount + data.failedCount > 0) {
        notifyWarning('Bulk lock summary', formatOutcomeBreakdown(data));
      }

      navigate('/tasks');
    },
    () => notifyError('Error', 'Tasks not locked')
  );

  const handleConfirmLock = (selectedRows: TaskInfo[]) => {
    bulkLock({ taskIds: selectedRows.map((row) => row.id) });
  };

  return (
    <BulkTaskActionDrawer
      title="Lock Tasks"
      actionButton={{
        label: 'Lock Tasks',
        color: 'warning',
        testId: 'lock-task-button',
      }}
      modalType="bulkLockTask"
      noRowsMessage="No Tasks available to lock in bulk."
      filterRow={filterLockableRow}
      renderConfirm={renderLockContent}
      confirmTitle={(count) => `Lock ${count} ${taskWord(count)}?`}
      actionStatus={bulkLockStatus}
      onConfirm={handleConfirmLock}
    />
  );
};
